import React from 'react';
import { PlusIcon, TrashIcon, MapPinIcon, CalendarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Route } from '../../types';
import Input from './Input';
import DateInput from './DateInput';
import DateDisplay from './DateDisplay';
import { isValidGregorianValue } from './hijriDate';

interface MultiRouteFormProps {
  routes: Route[];
  onRoutesChange: (routes: Route[]) => void;
  errors?: Record<string, string>;
  /** full: إنشاء رحلة | booking: حجز يدوي */
  variant?: 'full' | 'booking';
}

const MultiRouteForm: React.FC<MultiRouteFormProps> = ({
  routes,
  onRoutesChange,
  errors = {},
  variant = 'full',
}) => {
  const isBooking = variant === 'booking';

  const getBookingDateMin = (index: number): string | undefined => {
    // Don't block existing past dates when editing; only guide later routes
    if (index === 0) return undefined;
    const firstDate = routes[0]?.dateFrom;
    return firstDate || undefined;
  };

  const addRoute = () => {
    const newRoute: Route = {
      from: '',
      to: '',
      dateFrom: '',
      dateTo: '',
      timeFrom: '',
      timeTo: '',
      distance: isBooking ? 0 : 0,
      isActive: true,
    };
    onRoutesChange([...routes, newRoute]);
  };

  const removeRoute = (index: number) => {
    if (routes.length > 1) {
      onRoutesChange(routes.filter((_, i) => i !== index));
    }
  };

  const updateRoute = (index: number, field: keyof Route, value: string | number | boolean) => {
    const updatedRoutes = routes.map((route, i) => {
      if (i !== index) return route;

      const next = { ...route, [field]: value };

      if (isBooking) {
        if (field === 'dateFrom') next.dateTo = value as string;
        if (field === 'timeFrom') next.timeTo = value as string;
      }

      return next;
    });
    onRoutesChange(updatedRoutes);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <MapPinIcon className="w-5 h-5" />
          {isBooking && routes.length === 1 ? 'مسار الرحلة' : 'مسارات الرحلة'}
        </h3>
        <button
          type="button"
          onClick={addRoute}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          إضافة مسار
        </button>
      </div>

      {routes.map((route, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900">
              {isBooking && routes.length === 1 ? 'مسار الرحلة' : `مسار ${index + 1}`}
            </h4>
            {routes.length > 1 && (
              <button
                type="button"
                onClick={() => removeRoute(index)}
                className="flex items-center gap-1 px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
              >
                <TrashIcon className="w-4 h-4" />
                حذف
              </button>
            )}
          </div>

          {isBooking ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    من
                  </label>
                  <Input
                    value={route.from}
                    onChange={(e) => updateRoute(index, 'from', e.target.value)}
                    placeholder="مدينة المغادرة"
                    error={!!errors[`routes.${index}.from`]}
                  />
                  {errors[`routes.${index}.from`] && (
                    <p className="text-red-500 text-sm mt-1">{errors[`routes.${index}.from`]}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    إلى
                  </label>
                  <Input
                    value={route.to}
                    onChange={(e) => updateRoute(index, 'to', e.target.value)}
                    placeholder="مدينة الوصول"
                    error={!!errors[`routes.${index}.to`]}
                  />
                  {errors[`routes.${index}.to`] && (
                    <p className="text-red-500 text-sm mt-1">{errors[`routes.${index}.to`]}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <DateInput
                    label={
                      <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4 shrink-0" />
                        التاريخ
                      </span>
                    }
                    value={route.dateFrom}
                    onChange={(e) => updateRoute(index, 'dateFrom', e.target.value)}
                    error={!!errors[`routes.${index}.dateFrom`]}
                    min={getBookingDateMin(index)}
                  />
                  {errors[`routes.${index}.dateFrom`] && (
                    <p className="text-red-500 text-sm mt-1">{errors[`routes.${index}.dateFrom`]}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <ClockIcon className="w-4 h-4" />
                    وقت المغادرة
                  </label>
                  <Input
                    type="time"
                    value={route.timeFrom}
                    onChange={(e) => updateRoute(index, 'timeFrom', e.target.value)}
                    error={!!errors[`routes.${index}.timeFrom`]}
                  />
                  {errors[`routes.${index}.timeFrom`] && (
                    <p className="text-red-500 text-sm mt-1">{errors[`routes.${index}.timeFrom`]}</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">من</label>
                <Input
                  value={route.from}
                  onChange={(e) => updateRoute(index, 'from', e.target.value)}
                  placeholder="أدخل اسم المدينة"
                  error={!!errors[`routes.${index}.from`]}
                />
                {errors[`routes.${index}.from`] && (
                  <p className="text-red-500 text-sm mt-1">{errors[`routes.${index}.from`]}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">إلى</label>
                <Input
                  value={route.to}
                  onChange={(e) => updateRoute(index, 'to', e.target.value)}
                  placeholder="أدخل اسم المدينة"
                  error={!!errors[`routes.${index}.to`]}
                />
                {errors[`routes.${index}.to`] && (
                  <p className="text-red-500 text-sm mt-1">{errors[`routes.${index}.to`]}</p>
                )}
              </div>

              <div>
                <DateInput
                  label={
                    <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                      <CalendarIcon className="w-4 h-4 shrink-0" />
                      تاريخ البداية
                    </span>
                  }
                  value={route.dateFrom}
                  onChange={(e) => updateRoute(index, 'dateFrom', e.target.value)}
                  error={!!errors[`routes.${index}.dateFrom`]}
                  max={route.dateTo || undefined}
                />
                {errors[`routes.${index}.dateFrom`] && (
                  <p className="text-red-500 text-sm mt-1">{errors[`routes.${index}.dateFrom`]}</p>
                )}
              </div>

              <div>
                <DateInput
                  label={
                    <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                      <CalendarIcon className="w-4 h-4 shrink-0" />
                      تاريخ النهاية
                    </span>
                  }
                  value={route.dateTo}
                  onChange={(e) => updateRoute(index, 'dateTo', e.target.value)}
                  error={!!errors[`routes.${index}.dateTo`]}
                  min={route.dateFrom || undefined}
                />
                {errors[`routes.${index}.dateTo`] && (
                  <p className="text-red-500 text-sm mt-1">{errors[`routes.${index}.dateTo`]}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <ClockIcon className="w-4 h-4" />
                  وقت المغادرة
                </label>
                <Input
                  type="time"
                  value={route.timeFrom}
                  onChange={(e) => updateRoute(index, 'timeFrom', e.target.value)}
                  error={!!errors[`routes.${index}.timeFrom`]}
                />
                {errors[`routes.${index}.timeFrom`] && (
                  <p className="text-red-500 text-sm mt-1">{errors[`routes.${index}.timeFrom`]}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <ClockIcon className="w-4 h-4" />
                  وقت الوصول
                </label>
                <Input
                  type="time"
                  value={route.timeTo}
                  onChange={(e) => updateRoute(index, 'timeTo', e.target.value)}
                  error={!!errors[`routes.${index}.timeTo`]}
                />
                {errors[`routes.${index}.timeTo`] && (
                  <p className="text-red-500 text-sm mt-1">{errors[`routes.${index}.timeTo`]}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  المسافة (كم)
                </label>
                <Input
                  type="number"
                  value={route.distance}
                  onChange={(e) =>
                    updateRoute(index, 'distance', parseInt(e.target.value, 10) || 0)
                  }
                  placeholder="المسافة بالكيلومتر"
                  error={!!errors[`routes.${index}.distance`]}
                />
                {errors[`routes.${index}.distance`] && (
                  <p className="text-red-500 text-sm mt-1">{errors[`routes.${index}.distance`]}</p>
                )}
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={route.isActive}
                    onChange={(e) => updateRoute(index, 'isActive', e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-sm font-medium text-gray-700">مسار نشط</span>
                </label>
              </div>
            </div>
          )}

          {route.from && route.to && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <div className="text-sm text-blue-800">
                <strong>ملخص المسار:</strong> {route.from} ← {route.to}
                {route.dateFrom && isValidGregorianValue(route.dateFrom) && (
                  <span className="inline-flex flex-wrap items-center gap-2 mr-2">
                    <DateDisplay value={route.dateFrom} className="inline-flex" />
                    {route.timeFrom && <span>— {route.timeFrom}</span>}
                  </span>
                )}
                {!isBooking &&
                  route.dateTo &&
                  isValidGregorianValue(route.dateTo) && (
                    <span className="inline-flex flex-wrap items-center gap-2 mr-2">
                      <span>→</span>
                      <DateDisplay value={route.dateTo} className="inline-flex" />
                      {route.timeTo && <span>— {route.timeTo}</span>}
                    </span>
                  )}
                {!isBooking && route.distance > 0 && (
                  <span> | المسافة: {route.distance} كم</span>
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      {routes.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <MapPinIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>لا توجد مسارات. انقر على &quot;إضافة مسار&quot; لبدء إضافة المسارات.</p>
        </div>
      )}
    </div>
  );
};

export default MultiRouteForm;
